import { useTranslation } from "../../../i18n/useTranslation";
import styles from './invoices-all.module.scss';
import { useEffect, useState } from 'react';
import DialogTitle from '@mui/material/DialogTitle';
import { Pagination, Stack, TextField } from '@mui/material';
import classNames from 'classnames';
import { htmlId } from './invoices-all.service';
import globalStyles from '../../../global-styles.module.scss';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { border } from '../../../shared/dropdown/dropdown.service';
import { getAllInvoices } from '../delivery-documents.service';
import { IPaginatedList } from '../../../shared/models/paginated-list.interface';
import { IDocumentListView } from '../../../shared/models/document-list-view.interface';
import moment from 'moment';
import DatePickerApp from '../../../shared/date/date-picker';
import { IInvoicesAllSearch } from './interfaces/invoices-all-search.interface';

export interface IInvoicesAll {
    fetchPerUserOnly: boolean;
}

const InvoicesAll: React.FC<IInvoicesAll> = ({fetchPerUserOnly}) => {
    const { t } = useTranslation('translation'); 
    const [page, setPage] = useState<number>(1);
    const [count, setCount] = useState<number>(1);
    const [invoices, setInvoices] = useState<IDocumentListView[]>([]);
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [numberInvoice, setNumberInvoice] = useState<string>('');
    const [clickClearDate, setClickClearDate] = useState<boolean>(false);

    const getSearchParams = (page: number, isFromClearSearch: boolean = false) => {
        let _startDate: string = startDate;
        let _endDate: string = endDate;
        let _numberInvoice: string = numberInvoice;
        if (isFromClearSearch) {
            setStartDate(_startDate = '');
            setEndDate(_endDate = '');
            setNumberInvoice(_numberInvoice = '');
            setClickClearDate(true);
        }
        return {
            page: page,
            numberInvoice: _numberInvoice ? _numberInvoice : '',
            startDate: _startDate ? _startDate : '',
            endDate: _endDate ? _endDate : '',
            fetchPerUserOnly: fetchPerUserOnly
        } as IInvoicesAllSearch;
    }

    const getAndSetInvoices = (page: number, isFromClearSearch: boolean = false) => {
        getAllInvoices(getSearchParams(page, isFromClearSearch)).then((response) => {
            if (response) {
                const dto: IPaginatedList<IDocumentListView> = response.data;
                setInvoices(dto.items);
                setPage(dto.pageIndex + 1);
                setCount(Math.ceil(dto.totalCount/ dto.pageSize));
                if (isFromClearSearch) {
                    setNumberInvoice('');
                }
            }
        })
    }

    const handleChangePage = (page: number) => {
        getAndSetInvoices(page);
    }

    const handleSearch = () => {
        getAndSetInvoices(1);
    }

    const handleClearSearch = () => {
        getAndSetInvoices(1, true);
    }

    const handleChangeSearchValue = (event: any) => {
        setNumberInvoice(event.target.value);
    }

    const handleChangeStartDate = (date: string) => {
        setStartDate(date);
        setClickClearDate(false);
    }

    const handleChangeEndDate = (date: string) => {
        setEndDate(date);
        setClickClearDate(false);
    }

    useEffect(() => {
        getAndSetInvoices(page);
    }, []);

    return (
        <div>
            <DialogTitle>
                <div className={globalStyles.space_between_item}>
                    <label>
                        {t('INVOICES_ALL')}
                    </label>
                </div>
            </DialogTitle>
            <section className={classNames(styles.search_content, globalStyles.float_left_item)}>
                <div>
                    <label className={classNames(globalStyles.float_left_item, styles.field_label)}>
                        {t('NUMBER')}
                    </label>
                    <TextField
                        className={globalStyles.mr_3}
                        size="small"
                        value={numberInvoice}
                        onChange={handleChangeSearchValue}
                        inputProps={{style: {fontSize: 14}}}
                        InputLabelProps={{
                            sx: { color: "grey", "&.Mui-focused": { color: "grey" } },
                        }}
                        sx={{
                            width: "300px",
                            "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                                border: {border},
                            },
                            "&.Mui-focused fieldset": {
                                border: {border},
                                color: 'red'
                            },
                            "&:hover fieldset": {
                                border: {border},
                            },
                            "& .css-1jy569b-MuiFormLabel-root-MuiInputLabel-root.Mui-focused": {
                                color: 'red'
                            }
                            },
                        }}>
                    </TextField>
                </div>
                <div className={globalStyles.mr_3}>
                    <label className={classNames(globalStyles.float_left_item, styles.field_label)}>
                        {t('START_DATE')}
                    </label>
                    <DatePickerApp handleChange={handleChangeStartDate} clear={clickClearDate}/>
                </div>
                <div className={globalStyles.mr_3}>
                <label className={classNames(globalStyles.float_left_item, styles.field_label)}>
                    {t('END_DATE')}
                </label>
                    <DatePickerApp handleChange={handleChangeEndDate} clear={clickClearDate}/>
                </div>
                <button
                    id={`${htmlId}-search-btn`}
                    onClick={() => {handleSearch()}}
                    className={classNames(globalStyles.defult_btn, globalStyles.mr_1, styles.btn)}>
                    <SearchIcon className={globalStyles.mr_1}></SearchIcon>{t('SEARCH')}
                </button>
                <button
                    id={`${htmlId}-clear-search-btn`}
                    onClick={() => {handleClearSearch()}}
                    className={classNames(globalStyles.defult_btn, styles.btn)}>
                    <ClearIcon className={globalStyles.mr_1}></ClearIcon>{t('CLEAR_SEARCH')}
                </button>
            </section>
            <div>
                <div className={styles.container}>
                    <div className={styles.header_content}>
                        <div className={classNames(styles.cell_content, globalStyles.float_left_item)}>
                            <label>{t('NUMBER')}</label>
                        </div>
                        <div className={classNames(styles.cell_content, globalStyles.float_left_item)}>
                            <label>{t('DATE')}</label>
                        </div>
                        <div className={classNames(styles.cell_content, globalStyles.float_left_item)}>
                            <label>{t('ISSUER')}</label>
                        </div>
                        <div className={classNames(styles.cell_content, globalStyles.float_left_item)}>
                            <label>{t('CLIENT')}</label>
                        </div>
                    </div>
                    {invoices && invoices.map((item, index) => (
                        <div
                            className={`${classNames(styles.row_content, globalStyles.mb_1)} ${index % 2 !== 0 ? styles.shading_row : ''}`}
                            key={index}>
                            <div className={classNames(styles.cell_content, globalStyles.float_left_item)}>
                                <label>{item.documentNumber}</label>
                            </div>
                            <div className={classNames(styles.cell_content, globalStyles.float_left_item)}>
                                <label>{(moment(item.documentDate)).format('DD/MM/YYYY')}</label>
                            </div>
                            <div className={classNames(styles.cell_content, globalStyles.float_left_item)}>
                                <label>{item.firstContractorName}</label>
                            </div>
                            <div className={classNames(styles.cell_content, globalStyles.float_left_item)}>
                                <label>{item.secondContractorName}</label>
                            </div>
                        </div>    
                    ))}
                </div>
            </div>
            <Stack 
                alignItems="center"
                className={globalStyles.mb_2}>
                <Pagination count={count} boundaryCount={1} page={page} onChange={(_, page) => {handleChangePage(page)}}></Pagination>
            </Stack>
        </div>
    );
}
export default InvoicesAll;